import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardContent,
  Typography,
  Divider,
  Grid,
  useTheme,
  Button,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { useLocation } from "react-router-dom";
import { GET, POST, PUT } from "../Generic/CrudRequest";
import Icons from "../../Utils/Icons";
import { ContactEdit, ContactAdd } from "./ContactAddOrEdit";

export default function AddOrEditContact({ config }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(true);
  const [title, setTitle] = useState("");
  const [ContactData, setContactData] = useState(new ContactAdd());
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (location.pathname.includes("/Contactos/Editar")) {
      setTitle(t("contacts.edit"));
      const ContactId = getContactIdFromPath(location.pathname);
      getContactById(ContactId);
    } else {
      setTitle(t("contacts.add"));
    }
  }, [location.pathname, t]);

  const getContactIdFromPath = (pathname) => {
    const parts = pathname.split("/");
    return parts[parts.length - 1];
  };

  const getContactById = async (ContactId) => {
    
    const dataContact = await GET(`${config.url}/api/Contact/${ContactId}`);
    const updateDataContact = new ContactEdit(dataContact);

    setContactData(updateDataContact);
  };

  const addContact = async () => {
    const requiredFields = ["nombre", "correo"];
    const newErrors = {};

    if (ContactData.correo.trim() !== "" && !validateEmail(ContactData.correo)) {
      newErrors.correo = t("contacts.invalidEmail");
    }

    requiredFields.forEach((field) => {
      const fieldValue = getFieldByPath(ContactData, field);
      if (!fieldValue || fieldValue === "") {
        newErrors[field] = t("contacts.requiredField");
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    await POST(`${config.url}/api/Contact`, ContactData);
    navigate("/Contactos");
  };

  const editContact = async () => {
    const requiredFields = ["nombre", "correo"];
    const newErrors = {};

    if (ContactData.correo.trim() !== "" && !validateEmail(ContactData.correo)) {
      newErrors.correo = t("contacts.invalidEmail");
    }

    requiredFields.forEach((field) => {
      const fieldValue = getFieldByPath(ContactData, field);
      if (!fieldValue || fieldValue === "") {
        newErrors[field] = t("contacts.requiredField");
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    await PUT(`${config.url}/api/Contact`, ContactData);
    navigate("/Contactos");
  };

  const getFieldByPath = (object, path) => {
    const pathArray = path.split(".");
    let value = object;
    for (const prop of pathArray) {
      value = value[prop];
      if (value === undefined) break;
    }
    return value;
  };

  const handleSubmit = async () => {
    
    if (title === t("contacts.add")) {
      await addContact();
    } else if (title === t("contacts.edit")) {
      await editContact();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setContactData({
      ...ContactData,
      [name]: value,
    });

    if (name === "correo" && value.trim() !== "") {
      if (!validateEmail(value)) {
        setErrors({
          ...errors,
          [name]: t("contacts.invalidEmail"),
        });
      } else {
        setErrors({
          ...errors,
          [name]: "",
        });
      }
    }
  };

  const handleAccordionChange = () => {
    setExpanded(!expanded);
  };

  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  return (
    <Card
      variant="outlined"
      sx={{
        overflow: "hidden",
        boxShadow: "0 0 10px rgba(0, 0, 0, 0.2)",
        border: "1px solid rgba(0, 0, 0, 0.1)",
        borderRadius: 2,
      }}
    >
      <CardContent>
        <CardContent
          sx={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}
        >
          <Icons
            iconName="ArrowBack"
            fontSize="large"
            style={{
              backgroundColor: theme.palette.primary.main,
              color: "#fff",
              marginRight: 20,
            }}
            to="/Contactos"
            tooltip={t("buttons.back")}
          />
          <Typography variant="h3" sx={{ flex: "1", fontWeight: "bold" }}>
            {title} {t("contacts.title")}
          </Typography>
        </CardContent>
        <Divider sx={{ maxWidth: "calc(100% - 32px)", margin: "auto" }} />
        <CardContent sx={{ maxWidth: "calc(100% - 32px)", margin: "auto" }}>
          <Accordion
            expanded={expanded}
            onChange={handleAccordionChange}
            sx={{
              overflow: "hidden",
              boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)",
              border: "1px solid rgba(0, 0, 0, 0.1)",
              borderRadius: 2,
              maxWidth: "100%",
              margin: "auto",
            }}
          >
            <AccordionSummary
              sx={{ maxWidth: "calc(100% - 32px)", margin: "auto" }}
            >
              <Typography
                variant="h5"
                sx={{ color: theme.palette.primary.main }}
              >
                {t("contacts.contactInfo")}
              </Typography>
            </AccordionSummary>
            <Divider sx={{ maxWidth: "calc(100% - 64px)", margin: "auto" }} />
            <AccordionDetails
              sx={{ maxWidth: "calc(100% - 32px)", margin: "auto" }}
            >
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label={t("contacts.name")}
                      id="Nombre"
                      name="nombre"
                      value={ContactData.nombre}
                      variant="standard"
                      onChange={handleChange}
                      error={!!errors.nombre}
                      helperText={errors.nombre}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  id="correo"
                  name="correo"
                  label={t("contacts.email")}
                  variant="standard"
                  onChange={handleChange}
                  value={ContactData.correo}
                  error={!!errors.correo}
                  helperText={errors.correo}
                />
              </Grid>
                <Grid container justifyContent="flex-end" sx={{ marginTop: 2 }}>
                            <Button
                              variant="contained"
                              color="primary"
                              onClick={handleSubmit}
                            >
                              {t("contacts.save")}
                            </Button>
                          </Grid>
                </Grid>
              </CardContent>
            </AccordionDetails>
          </Accordion>
        </CardContent>
      </CardContent>
    </Card>
  );
}
